import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCircle, ShieldAlert } from 'lucide-react'
import { useUser } from '../contexts/UserContext'
import {
  approveAccount,
  approveVolunteerId,
  fetchVolunteerIdImageBlob,
  getPendingAccounts,
  getPendingVolunteerIds,
  PendingBusinessAccount,
  PendingVolunteerIdVerification,
  rejectAccount,
  rejectVolunteerId
} from '../api/admin'

const AdminApprovalsPage = () => {
  const navigate = useNavigate()
  const { user, isAuthenticated } = useUser()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [pendingAccounts, setPendingAccounts] = useState<PendingBusinessAccount[]>([])
  const [pendingVolunteerIds, setPendingVolunteerIds] = useState<PendingVolunteerIdVerification[]>([])

  const [approvingId, setApprovingId] = useState<string | null>(null)
  const [rejectingId, setRejectingId] = useState<string | null>(null)

  const [approvingVolunteerId, setApprovingVolunteerId] = useState<string | null>(null)
  const [rejectingVolunteerId, setRejectingVolunteerId] = useState<string | null>(null)
  const [openingVolunteerId, setOpeningVolunteerId] = useState<string | null>(null)

  const canAccess = isAuthenticated && user?.userType === 'ADMIN'

  const load = async () => {
    try {
      setLoading(true)
      setError(null)
      const [accounts, volunteerIds] = await Promise.all([getPendingAccounts(), getPendingVolunteerIds()])
      setPendingAccounts(accounts)
      setPendingVolunteerIds(volunteerIds)
    } catch (e: any) {
      setError(e?.message || 'Failed to load approvals')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    if (user?.userType !== 'ADMIN') {
      navigate('/')
      return
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.userType])

  const sortedAccounts = useMemo(() => {
    return [...pendingAccounts].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  }, [pendingAccounts])

  const handleApproveAccount = async (id: string) => {
    try {
      setApprovingId(id)
      await approveAccount(id)
      await load()
    } catch (e: any) {
      alert(e?.message || 'Failed to approve account')
    } finally {
      setApprovingId(null)
    }
  }

  const handleRejectAccount = async (id: string) => {
    const reason = window.prompt('Reason for rejection (optional):') || undefined
    const ok = window.confirm('Reject this account? (If it has no deliveries/requests tied to it, it will be deleted.)')
    if (!ok) return
    try {
      setRejectingId(id)
      await rejectAccount(id, reason)
      await load()
    } catch (e: any) {
      alert(e?.message || 'Failed to reject account')
    } finally {
      setRejectingId(null)
    }
  }

  const handleOpenVolunteerId = async (verificationId: string) => {
    try {
      setOpeningVolunteerId(verificationId)
      const blob = await fetchVolunteerIdImageBlob(verificationId)
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank', 'noopener,noreferrer')
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (e: any) {
      alert(e?.message || 'Failed to open ID photo')
    } finally {
      setOpeningVolunteerId(null)
    }
  }

  const handleApproveVolunteerId = async (verificationId: string) => {
    try {
      setApprovingVolunteerId(verificationId)
      const result = await approveVolunteerId(verificationId)
      if (result.emailStatus === 'sent') {
        alert('Volunteer ID approved. Approval email sent.')
      } else if (result.emailStatus === 'skipped') {
        alert(
          `Volunteer ID approved, but the approval email was not sent.\n\n${
            result.emailDetail ||
            'Email is not configured. On Railway Hobby, set RESEND_API_KEY + EMAIL_FROM (SMTP is blocked).'
          }`
        )
      } else if (result.emailStatus === 'failed') {
        alert(
          `Volunteer ID approved, but sending the approval email failed.\n\n${
            result.emailDetail ||
            'Check Railway logs. Hobby plans block SMTP — use Resend (RESEND_API_KEY) instead.'
          }`
        )
      }
      await load()
    } catch (e: any) {
      alert(e?.message || 'Failed to approve volunteer ID')
    } finally {
      setApprovingVolunteerId(null)
    }
  }

  const handleRejectVolunteerId = async (verificationId: string) => {
    const reason = window.prompt('Reason for rejection (optional):') || undefined
    try {
      setRejectingVolunteerId(verificationId)
      await rejectVolunteerId(verificationId, reason)
      await load()
    } catch (e: any) {
      alert(e?.message || 'Failed to reject volunteer ID')
    } finally {
      setRejectingVolunteerId(null)
    }
  }

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow p-6 max-w-md w-full text-center">
          <ShieldAlert className="h-10 w-10 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-semibold text-gray-900">Admin access required</h1>
          <p className="text-sm text-gray-600 mt-1">You don’t have permission to view this page.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Approvals</h1>
            <p className="text-sm text-gray-600 mt-1">Approve/reject business accounts and volunteer ID photos.</p>
          </div>
          <button onClick={load} className="btn-outline">
            Refresh
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-red-800 font-medium">Failed to load approvals</p>
            <p className="text-xs text-red-700 mt-1">{error}</p>
          </div>
        )}

        {/* Business Accounts */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Pending Business Accounts</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Approval requires: email verified, business verification submitted (PENDING), and onboarding completed for food banks. Phone verification is optional.
              </p>
            </div>
            <p className="text-sm text-gray-600">Pending ({sortedAccounts.length})</p>
          </div>

          {loading ? (
            <div className="p-6">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/3" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ) : sortedAccounts.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-3" />
              <p className="text-sm text-gray-700 font-medium">No pending business accounts</p>
              <p className="text-xs text-gray-500 mt-1">All good — nothing to approve right now.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {sortedAccounts.map((acct) => {
                const bvStatus = acct.businessVerification?.status
                const ready =
                  acct.emailVerified &&
                  bvStatus === 'PENDING' &&
                  (acct.userType !== 'FOOD_BANK' || acct.onboardingCompleted)

                return (
                  <div key={acct.id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {acct.firstName} {acct.lastName}{' '}
                        <span className="ml-2 text-xs font-medium text-gray-600">({acct.userType})</span>
                      </p>
                      <p className="text-xs text-gray-600 truncate">{acct.email}</p>
                      <p className="text-xs text-gray-600 truncate">{acct.phone}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Created: {new Date(acct.createdAt).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Email: {acct.emailVerified ? 'verified' : 'not verified'} • Phone: {acct.phoneVerified ? 'verified (optional)' : 'not verified (optional)'} •{' '}
                        Business verification: {bvStatus || 'not submitted'} • Onboarding: {acct.onboardingCompleted ? 'complete' : 'not complete'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleApproveAccount(acct.id)}
                        disabled={!ready || approvingId === acct.id || rejectingId === acct.id}
                        className={`btn-primary ${!ready || approvingId === acct.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                        title={!ready ? 'Waiting for required verifications before approval' : 'Approve'}
                      >
                        {approvingId === acct.id ? 'Approving...' : 'Approve'}
                      </button>
                      <button
                        onClick={() => handleRejectAccount(acct.id)}
                        disabled={approvingId === acct.id || rejectingId === acct.id}
                        className={`btn-outline text-red-700 border-red-200 hover:bg-red-50 ${
                          rejectingId === acct.id ? 'opacity-60 cursor-not-allowed' : ''
                        }`}
                      >
                        {rejectingId === acct.id ? 'Rejecting...' : 'Reject'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Volunteer IDs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mt-8">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">Pending Volunteer Photo IDs</p>
              <p className="text-xs text-gray-500 mt-0.5">Volunteers must be approved before claiming deliveries.</p>
            </div>
            <p className="text-sm text-gray-600">Pending ({pendingVolunteerIds.length})</p>
          </div>

          {loading ? (
            <div className="p-6">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-1/3" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          ) : pendingVolunteerIds.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-3" />
              <p className="text-sm text-gray-700 font-medium">No pending volunteer IDs</p>
              <p className="text-xs text-gray-500 mt-1">Nothing to review right now.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {pendingVolunteerIds.map((v) => (
                <div key={v.id} className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {v.user.firstName} {v.user.lastName}
                    </p>
                    <p className="text-xs text-gray-600 truncate">{v.user.email}</p>
                    <p className="text-xs text-gray-600 truncate">{v.user.phone}</p>
                    <p className="text-xs text-gray-500 mt-1">Submitted: {new Date(v.submittedAt).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleOpenVolunteerId(v.id)}
                      disabled={openingVolunteerId === v.id}
                      className={`btn-outline ${openingVolunteerId === v.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      {openingVolunteerId === v.id ? 'Opening...' : 'View Photo'}
                    </button>
                    <button
                      onClick={() => handleApproveVolunteerId(v.id)}
                      disabled={approvingVolunteerId === v.id || rejectingVolunteerId === v.id}
                      className={`btn-primary ${approvingVolunteerId === v.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      {approvingVolunteerId === v.id ? 'Approving...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => handleRejectVolunteerId(v.id)}
                      disabled={approvingVolunteerId === v.id || rejectingVolunteerId === v.id}
                      className={`btn-outline text-red-700 border-red-200 hover:bg-red-50 ${
                        rejectingVolunteerId === v.id ? 'opacity-60 cursor-not-allowed' : ''
                      }`}
                    >
                      {rejectingVolunteerId === v.id ? 'Rejecting...' : 'Reject'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminApprovalsPage


